import { Link, useLocation } from "wouter";
import { Home, Sun, Moon, LogOut, Settings, UserRound } from "lucide-react";
import { useTheme } from "next-themes";
import { useUser, useClerk } from "@clerk/react";
import { NotificationBell } from "@/components/NotificationBell";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// The single button used to get back to the inbox — shared between the full
// icon rail (Inbox.tsx, above its Everything/Email/Slack/Jira buttons) and
// the standalone rail shown on pages that have no rail of their own.
export function HomeRailButton() {
  return (
    <Link
      href="/"
      aria-label="Back to inbox"
      title="Back to inbox"
      className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
    >
      <Home className="h-6 w-6" />
    </Link>
  );
}

// Notifications, dark/light toggle, and the account popover (Settings,
// Sign out) — shared between the inbox's icon
// rail and the standalone rail on Account/Connections/Rules, so those
// controls are reachable everywhere, not just from the inbox.
export function UtilityRailFooter() {
  const [location] = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { resolvedTheme, setTheme } = useTheme();
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const displayName = user?.fullName || user?.primaryEmailAddress?.emailAddress || "Your account";
  const initials = (user?.firstName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0] || "A").toUpperCase();

  return (
    <div className="mt-auto flex flex-col items-center gap-1">
      <NotificationBell />

      <button
        type="button"
        data-testid="button-theme-toggle"
        onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
        aria-label={resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="button-account-menu"
            aria-label="Account"
            title={displayName}
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {user?.imageUrl ? <img src={user.imageUrl} alt="" className="h-full w-full object-cover" /> : initials}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="end" sideOffset={8} className="w-56 p-2">
          <div className="border-b border-border px-3 pb-2.5 pt-1">
            <div className="truncate font-medium text-foreground">{displayName}</div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <UserRound className="h-3 w-3" />
              {user?.primaryEmailAddress?.emailAddress || "Account settings"}
            </div>
          </div>
          <div className="mt-2 flex flex-col gap-1">
            <Link
              href="/settings"
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                location === "/settings"
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )}
            >
              <Settings className="h-4 w-4" />
              Settings
            </Link>
            <button
              type="button"
              onClick={() => signOut({ redirectUrl: basePath || "/" })}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Pages outside the inbox (Account, Connections, Rules) don't have folders,
// Slack channels, or Jira tickets to show — but users still need a way back
// and the same account/theme/notification controls, so they get this
// same-width rail with just Home up top and the utility footer at the
// bottom, instead of the full inbox nav.
export function HomeNavRail({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full">
      <div className="flex h-full w-16 shrink-0 flex-col items-center border-r border-border bg-card/20 py-3">
        <HomeRailButton />
        <UtilityRailFooter />
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
    </div>
  );
}
